import React, { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import Tabs from "app/components/Tabs";
import { useAdminSettingsTabs } from "app/routes/Facility/adminSettingsTabs";
import PagePanel from "app/components/PagePanel";
import ButtonVariant from "app/components/Button/ButtonVariant";
import SetupHint from "app/components/AcademySetup/SetupHint";
import useAcademySetupHint from "app/utils/hooks/useAcademySetupHint";
import CoverImage from "app/components/CoverImage";
import { toast } from "react-toastify";
import TextInput from "app/components/inputs/TextInput";
import PhoneInput from "react-phone-input-2";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { fetchGet, fetchPut } from "app/utils/request";
import { IUserPhone } from "app/interfaces/UserTypes";
import { useHistory } from "react-router-dom";
import {
  schedulingFetchGet,
  schedulingFetchPatch,
} from "app/utils/request/scheduling/fetch";
import UserAvatar from "app/components/UserAvatar";
import { cls } from "app/utils";
import { Loading } from "app/components/Wrappers";
import upload from "app/utils/uploader";
import { useTranslation } from "react-i18next";
import PreviewModal from "app/components/PublicProfiles/Facility/PreviewModal";
import FacilityPage from "app/components/PublicProfiles/Facility/FacilityPage";
import DefaultImage from "app/components/Programs/DefaultImage";
import { setDefaultImages } from "app/actions/scheduling/program";
import {
  Coach,
  Event,
  FacilityAPIResponse,
  CoachProfileAPI,
  ProgramAPI,
  transformCoachesResponse,
  transformProgramToEvent,
} from "app/components/PublicProfiles/services/api";

interface IFacility {
  id: string;
  phone: string;
  email: string;
  website: string;
  description: string;
  country: string;
  name: string;
}

interface IPublicUrls {
  publicCoverUrl: string;
  publicLogoUrl: string;
}

const LandingPage = () => {
  const history = useHistory();
  const tabs = useAdminSettingsTabs("landingPage");

  const [error, setError] = useState<{
    phone: string | null;
    email: string | null;
    website: string | null;
    description: string | null;
  }>({
    phone: null,
    email: null,
    website: null,
    description: null,
  });
  const [toastAlert, setToastAlert] = useState(null);
  const [toastErrorAlert, setToastErrorrAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [facility, setFacility] = useState<IFacility | null>(null);
  const [publicUrls, setPublicUrls] = useState<IPublicUrls>({
    publicCoverUrl: "",
    publicLogoUrl: "",
  });
  const [logoLoading, setLogoLoading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewCoaches, setPreviewCoaches] = useState<Coach[]>([]);
  const [previewEvents, setPreviewEvents] = useState<Event[]>([]);
  const [previewFacilityData, setPreviewFacilityData] = useState<{
    publicCalendarUrl?: string;
    publicProgramsUrl?: string;
    publicCalendar?: boolean;
  }>({});
  const facilityInfo = useSelector(
    (state: RootState) => state.user?.self?.scheduling?.facility,
    shallowEqual
  );
  const fileInputRef = React.useRef(null);
  const setupHintAnchorRef = React.useRef<HTMLSpanElement>(null);
  // Whether the coach came here from the setup checklist, read once on
  // mount: dismissing the coachmark drops the hint from router state, but
  // the follow-on hint on /manage_facility (enable the switch) should still
  // show after the save.
  const setupHint = useAcademySetupHint("academy_page");
  const arrivedFromSetup = React.useRef<boolean>(setupHint.active);
  const { t } = useTranslation();
  const [showDefaultImages, setShowDefaultImages] = useState(false);
  const dispatch = useDispatch();

  const loadFacility = async () => {
    try {
      const facilityInfoResponse = await fetchGet(
        `facilities/${facilityInfo?.id}`
      );
      setFacility((prev) => ({
        ...prev,
        id: facilityInfoResponse.id,
        phone: facilityInfoResponse.phone || "",
        email: facilityInfoResponse.email || "",
        website: facilityInfoResponse.website || "",
        description: facilityInfoResponse.description || "",
        country: facilityInfoResponse.country || "us",
        name: facilityInfoResponse.name || "",
      }));
    } catch (error) {
      console.error("Error loading facility info:", error);
    }
  };

  useEffect(() => {
    if (facilityInfo?.id) {
      loadFacility();
    }
  }, [facilityInfo?.id]);

  useEffect(() => {
    if (facilityInfo) {
      setPublicUrls((prev) => ({
        ...prev,
        publicCoverUrl: facilityInfo.public_cover_url,
        publicLogoUrl: facilityInfo.public_logo_url,
      }));
    }
  }, [facilityInfo]);

  const updateFacilityData = useCallback(
    ({ type, value }: { type: string; value: string }) => {
      const label = type.charAt(0).toUpperCase() + type.slice(1);
      if (type !== "name" && !value) {
        setError((prev) => ({
          ...prev,
          [type]: `${label} can't be blank`,
        }));
      } else if (value) {
        setError((prev) => ({
          ...prev,
          [type]: null,
        }));
      }

      setFacility((prev) => ({ ...prev, [type]: value }));
    },
    []
  );

  const onPhoneChange = (value: string, data: IUserPhone) => {
    if (!value) {
      setError((prev) => ({
        ...prev,
        phone: "Phone can't be blank",
      }));

      return;
    } else {
      setError((prev) => ({
        ...prev,
        phone: null,
      }));
    }
    const phoneCountry = data?.countryCode?.toUpperCase();

    setFacility((prev) => ({
      ...prev,
      phone: value,
      country: phoneCountry,
    }));
  };

  const isValidUrl = (value: string) =>
    value === "" || /^https?:\/\/[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/.test(value);

  const handleUrlInputUpdate = useCallback(
    ({ type, value }: { type: string; value: string }) => {
      const trimmedValue = value.replace(/\s+/g, "");

      const errorMessage = t(
        "Please provide a valid URL (e.g., https://example.com or www.example.com)."
      );

      if (trimmedValue === "") {
        setError((prev) => ({
          ...prev,
          website: "Website can't be blank",
        }));
      } else if (!isValidUrl(trimmedValue)) {
        setError((prev) => ({
          ...prev,
          website: errorMessage,
        }));
      } else {
        setError((prev) => ({
          ...prev,
          website: null,
        }));
      }

      setFacility((prev) => ({ ...prev, [type]: trimmedValue }));
    },
    [t, isValidUrl]
  );

  const hasErrors = (errorState: {
    phone: string | null;
    email: string | null;
    website: string | null;
    description: string | null;
  }) => {
    return Object.values(errorState).some((error) => error !== null);
  };

  const validateMandatoryFields = () => {
    let noErrors: boolean = true;

    if (!facility.description) {
      setError((prev) => ({
        ...prev,
        description: "Description can't be blank",
      }));
      noErrors = false;
    }

    if (!facility.email) {
      setError((prev) => ({
        ...prev,
        email: "Email can't be blank",
      }));
      noErrors = false;
    }

    if (!facility.website) {
      setError((prev) => ({
        ...prev,
        website: "Website can't be blank",
      }));
      noErrors = false;
    }

    if (facility.phone.length < 5) {
      setError((prev) => ({
        ...prev,
        phone: "Phone can't be blank",
      }));
      noErrors = false;
    }

    return noErrors;
  };

  const updateFacility = async () => {
    if (!validateMandatoryFields() || hasErrors(error)) {
      setToastAlert(null);
      setToastErrorrAlert(
        t("Please resolve the errors before updating your account.")
      );
      return;
    }

    setLoading(true);
    try {
      if (facility) {
        await fetchPut(`facilities/${facilityInfo?.id}`, {
          facility: { ...facility, public_landing_page_enabled: true },
        });
      }

      if (facilityInfo) {
        await schedulingFetchPatch(
          `facilities/${facilityInfo?.id}/update_public_images`,
          publicUrls
        );
      }

      setToastErrorrAlert(null);
      setToastAlert(t("Your account was successfully updated."));
      setLoading(false);
      // The checklist's designed follow-on for academy_page is the public
      // landing page switch in Admin Settings → General (its toggle writes
      // both the core and scheduling records and owns the completion rail),
      // so a coach who came from the checklist is handed on to it.
      history.push(
        "/manage_facility",
        arrivedFromSetup.current ? { academySetupHint: "academy_page" } : undefined,
      );
    } catch (error) {
      console.error("Error during update:", error);
      setToastErrorrAlert(
        t(
          "An error occurred while updating your account. Please try again later."
        )
      );
      setToastAlert(null);
      setLoading(false);
    }
  };

  const onCoverChange = (avatar: { id: string; url: string }) => {
    setPublicUrls((prev) => ({
      ...prev,
      publicCoverUrl: avatar?.url,
    }));
  };

  const deleteCoverHandler = () => {
    setPublicUrls((prev) => ({
      ...prev,
      publicCoverUrl: null,
    }));
  };

  const loadAvatarSrc = useCallback(() => {
    if (logoLoading) {
      return null;
    }

    if (publicUrls?.publicLogoUrl) {
      return publicUrls?.publicLogoUrl;
    }

    return publicUrls?.publicLogoUrl || "default";
  }, [publicUrls, logoLoading]);

  const handleLogoClick = () => {
    fileInputRef.current.click();
  };

  const deleteLogoHandler = () => {
    setPublicUrls((prev) => ({
      ...prev,
      publicLogoUrl: null,
    }));
  };

  const loadPreviewData = async () => {
    // Extract facility UUID from publicLandingPageURL
    // Example: "https://staging.coachnow.io/facility/golfing-paradise-v3/a4ba6cf4cc08b4d6b7546b53c3"
    // We need the last segment: "a4ba6cf4cc08b4d6b7546b53c3"
    const facilityUUID = facilityInfo?.publicLandingPageURL?.split('/').pop();

    if (!facilityUUID) {
      console.error("No facility UUID available from publicLandingPageURL");
      return;
    }

    let facilityName = facilityInfo.name;
    let originalCoachProfiles: CoachProfileAPI[] = [];

    try {
      // Fetch facility data from Scheduling API using the extracted UUID
      const facilityResponse = await schedulingFetchGet<FacilityAPIResponse>(
        `facilities/${facilityUUID}`,
        {},
        {},
        undefined,
        true // public_url = true
      );

      const facilityData = Array.isArray(facilityResponse)
        ? facilityResponse[0]
        : facilityResponse;

      facilityName = facilityData.name;
      originalCoachProfiles = facilityData.coachProfiles || [];

      const coaches = transformCoachesResponse(
        originalCoachProfiles,
        facilityData.name
      );

      setPreviewCoaches(coaches);

      // Store facility calendar and programs URLs
      setPreviewFacilityData({
        publicCalendarUrl: facilityData.publicCalendarUrl,
        publicProgramsUrl: facilityData.publicProgramsUrl,
        publicCalendar: facilityData.publicCalendar,
      });
    } catch (error) {
      console.error("Error loading preview coaches:", error);
      setPreviewCoaches([]);
      return;
    }

    // Fetch programs/events from Scheduling API using the extracted UUID
    try {
      const programsResponse = await schedulingFetchGet(
        "/programs",
        { facility_id: facilityUUID },
        {},
        undefined,
        true // public_url = true
      );

      const programsData = programsResponse?.data?.active || [];
      const transformedEvents = programsData.map((program: ProgramAPI) =>
        transformProgramToEvent(program, facilityName, originalCoachProfiles)
      );

      setPreviewEvents(transformedEvents);
    } catch (error) {
      console.error("Error loading preview events:", error);
      setPreviewEvents([]);
    }
  };

  const handlePreview = async () => {
    setIsPreviewOpen(true);
    await loadPreviewData();
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  const RenderFacilityLogo = () => {
    return (
      <div className={styles.avatar_container} onClick={handleLogoClick}>
        <UserAvatar
          sizeWidth={120}
          sizeHeight={120}
          altName="logo"
          srcName={loadAvatarSrc()}
          userName={facilityInfo?.name}
          className={cls("avatar avatar-lg font-lg", styles.avatar)}
        />
        <Loading
          isLoading={logoLoading}
          loadType="spinner"
          className={styles.avatar_loader}
        />

        {publicUrls?.publicLogoUrl &&
          !String(publicUrls.publicLogoUrl).includes("default") && (
            <span
              className={styles.delete_icon_container}
              onClick={(e) => {
                e.stopPropagation();
                deleteLogoHandler();
              }}
            >
              <i className={"ico ico-trash-outline"} />
            </span>
          )}
      </div>
    );
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const file = event.target.files[0];
      const filename = file.name;
      const type = "avatar";
      setLogoLoading(true);

      const uploadResult = await upload(file, filename, type, "avatar");
      setPublicUrls((prev) => ({
        ...prev,
        publicLogoUrl: uploadResult.url,
      }));

      setLogoLoading(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      setLogoLoading(false);
    } finally {
      setLogoLoading(false);
    }
  };

  const UploadNewMediaRender = () => {
    return (
      <>
        <input
          type="file"
          accept="image/*"
          className={styles.file_uploader_input}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </>
    );
  };

  const loadDefaultImages = () => {
    schedulingFetchGet("/default_images").then((response) => {
      dispatch(setDefaultImages(response));
    });
  };

  const onClickSelectOurList = (e) => {
    e.stopPropagation();
    loadDefaultImages();
    setShowDefaultImages(true);
  };

  const handleCloseDefaultImagesModal = () => {
    setShowDefaultImages(false);
  };

  const onCropComplete = async (blobURL) => {
    setPublicUrls((prev) => ({
      ...prev,
      publicCoverUrl: blobURL,
    }));
    setShowDefaultImages(false);
  };

  useEffect(() => {
    if (toastAlert) {
      toast.success(toastAlert);
    }

    if (toastErrorAlert) {
      toast.error(toastErrorAlert);
    }

    return () => {
      setToastAlert(null);
      setToastErrorrAlert(null);
    };
  }, [toastAlert, toastErrorAlert]);

  return (
    <div className="admin-settings-container">
      <div className={styles.title}>
        <h1>{t("Admin Settings")}</h1>
      </div>
      <div>
        <div className={styles.tabs_container}>
          <Tabs tabs={tabs} scrollable />
        </div>
        <PagePanel
          title={t("Your Page")}
          titleStyles={styles.header}
          headerChildren={
            <ButtonVariant
              title={t("Preview")}
              buttonType="secondary-filled"
              onClickFunc={handlePreview}
              buttonStyles={styles.previewButton}
            />
          }
        >
          {
            <>
              <div className={styles.profile_container}>
                <CoverImage
                  onChange={onCoverChange}
                  image={publicUrls?.publicCoverUrl}
                  onDelete={deleteCoverHandler}
                  containerStyle={styles.cover_image}
                  noImageChild={
                    <>
                      <p>
                        {t("Cover photo will appear at the top of your page")}
                      </p>
                      <div className={styles.select_cover_container}>
                        <ButtonVariant
                          title={t("Upload")}
                          buttonType="secondary-filled"
                          icon={<i className="ico ico-download" />}
                          childStyles={styles.upload_button_child}
                          buttonStyles={styles.upload_button}
                        />
                        <span className={styles.text}>{`or`}</span>
                        <ButtonVariant
                          title={t("select from our list")}
                          buttonType="secondary-filled"
                          icon={<i className="ico ico-download" />}
                          childStyles={styles.upload_button_child}
                          buttonStyles={styles.upload_button}
                          onClickFunc={onClickSelectOurList}
                        />
                      </div>
                    </>
                  }
                  childStyle={styles.no_image_wrapper}
                />
                <div className={styles.avatar_container}>
                  <RenderFacilityLogo />
                  <UploadNewMediaRender />
                </div>
              </div>
              <div className={styles.page_wrapper}>
                <div className={styles.info_container}>
                  <div className={styles.left}>
                    <span
                      ref={setupHintAnchorRef}
                      className={styles.space_from_title}
                    >
                      <SetupHint
                        itemKey="academy_page"
                        anchorRef={setupHintAnchorRef}
                        placement="bottom-start"
                      />
                      <TextInput
                        label={t("Facility Name")}
                        value={facility?.name}
                        onChange={(e) =>
                          updateFacilityData({
                            type: "name",
                            value: e.target.value,
                          })
                        }
                      />
                    </span>
                    <span className={styles.space_from_title}>
                      <label htmlFor="about">{t("About")}</label>
                      <textarea
                        id="about"
                        value={facility?.description}
                        rows={25}
                        onChange={(e) =>
                          updateFacilityData({
                            type: "description",
                            value: e.target.value,
                          })
                        }
                        placeholder={t("About")}
                      />
                      <p className={styles.error}>{error?.description}</p>
                    </span>
                  </div>
                  <div className={styles.right}>
                    <span className={styles.contact_info}>
                      <h2>{t("Contact Information")}</h2>
                      <p>
                        {t(
                          "This information will be displayed on your public page"
                        )}
                      </p>
                    </span>
                    <span className={styles.phone_container}>
                      <label htmlFor="phone">{t("Phone Number")}</label>
                      <PhoneInput
                        onChange={onPhoneChange}
                        inputStyle={{ width: "100%", height: "42px" }}
                        enableSearch
                        disableSearchIcon
                        searchClass={styles.search_class}
                        inputClass={styles.input_class}
                        buttonClass={styles.button_class}
                        dropdownClass={styles.dropdown_class}
                        value={facility?.phone}
                      />
                      <p className={styles.error}>{error?.phone}</p>
                    </span>
                    <span className={styles.email_wrapper}>
                      <label htmlFor="email">{t("Email")}</label>
                      <input
                        type="email"
                        id="email"
                        value={facility?.email}
                        onChange={(e) =>
                          updateFacilityData({
                            type: "email",
                            value: e.target.value,
                          })
                        }
                        placeholder={t("Email")}
                      />
                      <p className={styles.error}>{error?.email}</p>
                    </span>
                    <span className={styles.website}>
                      <label htmlFor="booking_service">{t("Website")}</label>
                      <input
                        type="text"
                        id="website"
                        value={facility?.website}
                        onChange={(e) =>
                          handleUrlInputUpdate({
                            type: "website",
                            value: e.target.value,
                          })
                        }
                        placeholder={t("Website")}
                      />
                      <p className={styles.error}>{error?.website}</p>
                    </span>
                  </div>
                </div>
                <ButtonVariant
                  title={t("Save changes")}
                  buttonType="primary"
                  onClickFunc={updateFacility}
                  buttonStyles={styles.submit_button}
                  loading={loading}
                />
              </div>
            </>
          }
        </PagePanel>
      </div>

      <PreviewModal isOpen={isPreviewOpen} onClose={handleClosePreview}>
        <FacilityPage
          previewMode={true}
          previewData={{
            facility: {
              name: facility?.name || "",
              description: facility?.description || "",
              phone: facility?.phone || "",
              email: facility?.email || "",
              website: facility?.website || "",
            },
            publicLogoUrl: publicUrls?.publicLogoUrl || "",
            publicCoverUrl: publicUrls?.publicCoverUrl || "",
            publicCalendarUrl: previewFacilityData.publicCalendarUrl,
            publicProgramsUrl: previewFacilityData.publicProgramsUrl,
            publicCalendar: previewFacilityData.publicCalendar,
            coaches: previewCoaches,
            events: previewEvents,
          }}
        />
      </PreviewModal>
      {showDefaultImages && (
        <DefaultImage
          uploadedImageURL={publicUrls?.publicCoverUrl}
          onConfirm={onCropComplete}
          needsCrop={false}
          showModal={handleCloseDefaultImagesModal}
          uploadedImage={null}
        />
      )}
    </div>
  );
};

export default LandingPage;
